import { notification } from '@surecompanies/core_components'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { extractApiErrorMessage } from 'helpers/api-error-message'
import { browserHistory } from 'react-router'
import { updateDemoCompany } from '../../../DemoCustomer/api/demo-customer.api'
import { DemoCustomerCreateFormData } from '../../../DemoCustomer/demoCustomer.schema'
import { demoCompanyViewPath } from '../../../paths'

/**
 * Updates a demo company via PUT /api/v1/customers/demo/:id.
 *
 * The endpoint only accepts the two editable fields and does the rest
 * server-side: it re-applies the "ZZ - DEMO - " prefix, upserts the SURECO
 * contact in place without disturbing Billing/HR contacts, and drops that
 * contact when every field is blank. Payroll cycle and the enrollment dates
 * are untouched by design, so nothing else needs to be sent.
 */
export default function useUpdateDemoCompany({ id }: { id: string }) {
  const queryClient = useQueryClient()

  const submit = async (formData: DemoCustomerCreateFormData) =>
    updateDemoCompany(id, {
      companyName: formData.companyName,
      // Always sent — omitting it would make the server preserve the existing
      // contact rather than clear it. All four sub-fields are included with
      // ?? '' (not || undefined) so empty values survive JSON.stringify and
      // reach the API: the server clears a field when it receives ''.
      salesContact: {
        fullName: formData.salesContact?.fullName ?? '',
        title: formData.salesContact?.title ?? '',
        phone: formData.salesContact?.phone ?? '',
        email: formData.salesContact?.email ?? '',
      },
    })

  const { mutate, isPending } = useMutation({
    mutationFn: submit,
    mutationKey: ['updateDemoCompany', id],
    onSuccess: () => {
      notification.success({ message: 'Demo company updated successfully' })
      queryClient.invalidateQueries({ queryKey: ['getCustomer', id] })
      queryClient.invalidateQueries({ queryKey: ['getDemoCompanies'] })
      browserHistory.push(`/${demoCompanyViewPath(id)}`)
    },
    onError: (error: any) => {
      notification.error({
        message: 'Error updating demo company',
        description: extractApiErrorMessage(error),
      })
    },
  })

  return { mutate, isPending }
}
